import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { ClockIcon, UserIcon } from "lucide-react";
import { getBusinessHoursStatus } from "@/utils/businessHours";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { buildWhatsAppLink, GLOBAL_WHATSAPP_NUMBER, normalizeWhatsAppNumber } from "@/utils/whatsapp";
import { useLanguage } from "@/contexts/LanguageContext";

const localeMap: Record<string, string> = { pt: "pt-BR", en: "en-US", es: "es-ES" };

const BusinessHoursBadge = () => {
  const { locale, t } = useLanguage();
  const [status, setStatus] = useState(getBusinessHoursStatus());
  const [companyPhone, setCompanyPhone] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loadingPhone, setLoadingPhone] = useState(false);

  const formattedTime = status.nextBusinessHour.toLocaleTimeString(localeMap[locale] || "pt-BR", { hour: "2-digit", minute: "2-digit" });
  const titleMessage = status.isBusinessHours ? t.businessHours.messageUntil(formattedTime) : t.businessHours.messageBot(formattedTime);

  useEffect(() => {
    // Atualizar status a cada minuto
    const interval = setInterval(() => {
      setStatus(getBusinessHoursStatus());
    }, 60000); // Atualizar a cada minuto

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadPhone = async () => {
      const userRaw = getUser();
      if (!userRaw) return;

      try {
        const user = JSON.parse(userRaw);
        setUserRole(user.role || null);
        if (!user.company_id) return;
        setLoadingPhone(true);
        const token = getToken();
        const response = await fetch(getApiEndpoint(`/api/companies/${user.company_id}`), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.company && data.company.phone) {
            setCompanyPhone(data.company.phone);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar telefone da empresa:", error);
      } finally {
        setLoadingPhone(false);
      }
    };

    loadPhone();
  }, []);

  const handleClick = () => {
    if (!status.isBusinessHours) return;
    const isExternal = userRole === "external";
    const companyDigits = normalizeWhatsAppNumber(companyPhone);
    const targetNumber = isExternal ? (companyDigits || GLOBAL_WHATSAPP_NUMBER) : GLOBAL_WHATSAPP_NUMBER;
    if (!targetNumber) return;
    const message = "Olá, preciso de atendimento humano.";
    window.open(buildWhatsAppLink(targetNumber, message), "_blank");
  };

  return (
    <>
      <Badge
        variant={status.isBusinessHours ? "default" : "secondary"}
        className={`flex items-center gap-1 cursor-pointer ${
          status.isBusinessHours
            ? "bg-green-600 hover:bg-green-700"
            : "bg-orange-600 hover:bg-orange-700"
        }`}
        title={titleMessage}
        onClick={handleClick}
      >
        {status.isBusinessHours ? (
          <>
            <UserIcon className="h-3 w-3" />
            <span className="text-xs">{t.businessHours.badgeHuman}</span>
          </>
        ) : (
          <>
            <ClockIcon className="h-3 w-3" />
            <span className="text-xs">{t.businessHours.badgeOff}</span>
          </>
        )}
      </Badge>

      {!status.isBusinessHours && (
        <Badge variant="secondary" className="hidden">
          {/* placeholder to keep JSX balanced */}
        </Badge>
      )}
    </>
  );
};

export default BusinessHoursBadge;

